import React from "react";
import { Radio, TriangleAlert, MapPin, Clock, Siren, CheckCircle2, Loader2 } from "lucide-react";
import { fileUrl } from "../../lib/api";

const NEXT = { pending: "acknowledged", acknowledged: "enroute", enroute: "on_scene", on_scene: "cleared" };
const NEXT_LABEL = { pending: "Acknowledge", acknowledged: "En Route", enroute: "On Scene", on_scene: "Clear incident" };
const STATUS_META = {
  pending: { c: "#FFC726", label: "NEW DISPATCH" }, acknowledged: { c: "#5B9BFF", label: "ACKNOWLEDGED" },
  enroute: { c: "#38BDF8", label: "EN ROUTE" }, on_scene: { c: "#22E0A1", label: "ON SCENE" },
};
const SEV = { high: "#FF5C7A", medium: "#FFC726", low: "#8194B2" };

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

const DutyFeed = ({ dispatches, incidents, loading, onUpdate, updatingId }) => {
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => { const t = setInterval(force, 15000); return () => clearInterval(t); }, []);

  return (
    <div className="go-scroll min-h-full overflow-y-auto px-5 pb-28 pt-[max(env(safe-area-inset-top),16px)]">
      <header className="go-fade-up mb-4">
        <h1 className="font-head text-2xl font-extrabold text-white">Duty Feed</h1>
        <p className="mt-1 text-sm text-[#8194B2]">Your dispatches and live island incidents.</p>
      </header>

      <h3 className="font-head mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#67799A]"><Siren size={15} color="#FFC726" /> My dispatches</h3>
      {loading && <div className="flex justify-center py-8 text-[#67799A]"><Loader2 className="animate-spin" /></div>}
      {!loading && dispatches.length === 0 && (
        <div className="go-card rounded-2xl px-4 py-6 text-center text-sm text-[#67799A]" data-testid="no-dispatches">No active dispatches. Standing by.</div>
      )}
      <div className="space-y-3">
        {dispatches.map((d) => {
          const meta = STATUS_META[d.status] || STATUS_META.pending;
          const urgent = d.priority === "urgent";
          return (
            <div key={d.id} className="go-card overflow-hidden rounded-2xl" style={{ borderLeft: `4px solid ${meta.c}` }} data-testid={`dispatch-${d.id}`}>
              <div className="p-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-extrabold" style={{ background: `${meta.c}22`, color: meta.c }}>{meta.label}</span>
                  {urgent && <span className="rounded-full bg-[#FF5C7A]/20 px-2 py-0.5 text-[10px] font-extrabold text-[#FF7089]">URGENT</span>}
                </div>
                <div className="font-head text-base font-bold text-white">{d.title}</div>
                <div className="mt-1 flex items-center gap-3 text-xs text-[#8194B2]">
                  <span className="flex items-center gap-1"><MapPin size={12} /> {d.location}</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {ago(d.created_at)} ago</span>
                </div>
                {d.note && <div className="mt-2 text-xs text-[#A9B8D2]">“{d.note}”</div>}
                {NEXT[d.status] && (
                  <button onClick={() => onUpdate(d.id, NEXT[d.status])} disabled={updatingId === d.id}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold text-[#06133A] transition-transform active:scale-[0.98] disabled:opacity-60"
                    style={{ background: meta.c }} data-testid={`dispatch-action-${d.id}`}>
                    {updatingId === d.id ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} strokeWidth={2.6} />}
                    {NEXT_LABEL[d.status]}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="font-head mb-3 mt-7 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#67799A]"><TriangleAlert size={15} color="#FF7089" /> Active incidents</h3>
      <div className="space-y-2.5">
        {incidents.length === 0 && <div className="go-card rounded-2xl px-4 py-5 text-center text-sm text-[#67799A]">No active incidents reported.</div>}
        {incidents.map((i) => (
          <div key={i.id} className="go-card rounded-2xl p-3.5" data-testid={`incident-${i.id}`}>
            <div className="flex items-start gap-3">
              {i.photo_path && <img src={fileUrl(i.photo_path)} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: SEV[i.severity] || SEV.low }} />
                  <span className="font-head text-sm font-bold text-white">{i.type}</span>
                  {i.verified && <span className="rounded-full bg-[#22E0A1]/15 px-1.5 text-[10px] font-bold text-[#22E0A1]">VERIFIED</span>}
                </div>
                <div className="mt-0.5 text-xs text-[#8194B2]">{i.location}</div>
                {i.description && <div className="mt-1 text-xs text-[#A9B8D2] line-clamp-2">{i.description}</div>}
                <div className="mt-1 text-[11px] text-[#67799A]">{i.reporter_name} · {i.minutes_ago}m ago</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DutyFeed;
